import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { X, Send, Square, RotateCcw, FolderOpen } from "lucide-react";
import { ChatModeSelector } from "./ChatModeSelector";
import type { ChatMode } from "./ChatModeSelector";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useChatContext } from "./ChatProvider";
import { MessageBubble } from "./MessageBubble";
import { QuickPrompts, getProjectPrompts } from "./QuickPrompts";
import { PromptRefiner } from "./PromptRefiner";
import { EnhancePromptButton } from "./EnhancePromptButton";
import type { Message } from "@shared/types";

interface ChatPanelProps {
  onClose?: () => void;
  mode?: "page" | "fab";
}

export function ChatPanel({ onClose, mode = "fab" }: ChatPanelProps) {
  const {
    messages,
    isStreaming,
    currentResponse,
    error,
    sendMessage,
    stopStreaming,
    clearChat,
    submitFeedback,
  } = useChatContext();

  const [inputValue, setInputValue] = useState("");
  const [refineRequest, setRefineRequest] = useState<string | null>(null);
  const [chatMode, setChatMode] = useState<ChatMode>(() => {
    try {
      const stored = localStorage.getItem("mentor-chat-mode");
      return stored === "cowork" ? "cowork" : "claude-code";
    } catch {
      return "claude-code";
    }
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [activeProject, setActiveProject] = useState<string>(() => {
    try {
      return localStorage.getItem("mentor-selected-project") ?? "";
    } catch {
      return "";
    }
  });

  // Sync project from localStorage (in case other components update it)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "mentor-selected-project") {
        setActiveProject(e.newValue ?? "");
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    localStorage.setItem("mentor-chat-mode", chatMode);
  }, [chatMode]);

  // Scroll to bottom on new messages or streaming (respeita prefers-reduced-motion)
  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    bottomRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "end",
    });
  }, [messages, currentResponse]);

  // Focus textarea on open
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text) return;
    setInputValue("");
    await sendMessage(text);
  };

  const panelRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape" && mode === "fab" && onClose) {
      onClose();
    }
  };

  // Focus trap for fab mode: cycle Tab between focusable elements
  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (mode !== "fab") return;
      if (e.key === "Escape" && onClose) {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0] as HTMLElement | undefined;
      const last = focusable[focusable.length - 1] as HTMLElement | undefined;
      if (!first || !last) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [mode, onClose],
  );

  // Build display messages including streaming placeholder (memoized)
  const displayMessages = useMemo(() => {
    const msgs: Array<Message & { isStreamingMsg?: boolean }> = [...messages];
    if (isStreaming && currentResponse) {
      msgs.push({
        id: "__streaming__",
        conversation_id: "",
        role: "assistant",
        content: currentResponse,
        sources: null,
        feedback: null,
        created_at: new Date().toISOString(),
        isStreamingMsg: true,
      });
    }
    return msgs;
  }, [messages, isStreaming, currentResponse]);

  const isEmpty = messages.length === 0 && !isStreaming;

  const isPage = mode === "page";

  const containerClass = isPage
    ? "flex h-full w-full flex-col overflow-hidden neu-card"
    : "fixed bottom-20 right-4 z-40 flex h-[80vh] w-[28rem] flex-col overflow-hidden rounded-2xl neu-card shadow-2xl max-sm:inset-0 max-sm:bottom-0 max-sm:right-0 max-sm:h-full max-sm:w-full max-sm:rounded-none";

  const motionProps = isPage
    ? {}
    : {
        initial: { opacity: 0, y: 80, scale: 0.96 } as const,
        animate: { opacity: 1, y: 0, scale: 1 } as const,
        exit: { opacity: 0, y: 80, scale: 0.96 } as const,
        transition: { type: "spring" as const, damping: 28, stiffness: 320 },
      };

  const a11yProps = isPage
    ? {}
    : {
        role: "dialog" as const,
        "aria-modal": true as const,
        "aria-label": "Chat Mentor",
      };

  return (
    <motion.div
      ref={panelRef}
      onKeyDown={handlePanelKeyDown}
      {...motionProps}
      {...a11yProps}
      className={containerClass}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-foreground">Mentor IA</h2>
          {activeProject && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              <FolderOpen className="h-3 w-3" />
              {activeProject.split("/").pop()}
            </span>
          )}
          <ChatModeSelector mode={chatMode} onChange={setChatMode} />
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 sm:h-7 sm:w-7"
            onClick={() => {
              if (messages.length === 0 || window.confirm("Limpar conversa atual?")) {
                clearChat();
              }
            }}
            aria-label="Nova conversa"
          >
            <RotateCcw className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
          </Button>
          {!isPage && onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 sm:h-7 sm:w-7"
              onClick={onClose}
              aria-label="Fechar chat"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Prompt Refiner */}
      <PromptRefiner initialPrompt={refineRequest} onConsumed={() => setRefineRequest(null)} />

      {/* Messages */}
      <ScrollArea className="min-h-0 flex-1">
        <div ref={scrollRef} className="flex flex-col gap-3 p-4" aria-live="polite">
          {isEmpty ? (
            <QuickPrompts onSelect={(p) => sendMessage(p)} onRefine={(p) => setRefineRequest(p)} />
          ) : (
            displayMessages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isStreaming={"isStreamingMsg" in msg && msg.isStreamingMsg === true}
                streamingContent={
                  "isStreamingMsg" in msg && msg.isStreamingMsg === true
                    ? currentResponse
                    : undefined
                }
                onFeedback={msg.role === "assistant" ? submitFeedback : undefined}
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {/* Error */}
      {error && (
        <div className="border-t border-destructive/20 bg-destructive/10 px-4 py-2 text-xs text-destructive" role="alert">
          {error}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pergunte sobre skills..."
            aria-label="Mensagem para o Mentor IA"
            className="min-h-[44px] max-h-[160px] flex-1 resize-none bg-background text-base sm:text-sm"
            rows={1}
          />
          <EnhancePromptButton
            value={inputValue}
            onChange={setInputValue}
            disabled={isStreaming}
          />
          {isStreaming ? (
            <Button
              size="icon"
              variant="outline"
              className="h-10 w-10 shrink-0"
              onClick={stopStreaming}
              aria-label="Parar"
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={handleSend}
              disabled={!inputValue.trim()}
              aria-label="Enviar"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
        {!isEmpty && (
          <div className="flex gap-1.5 overflow-x-auto pt-2">
            {getProjectPrompts().slice(0, 3).map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => sendMessage(prompt)}
                className="shrink-0 rounded-full border border-border/50 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
              >
                {prompt.length > 40 ? prompt.slice(0, 37) + "..." : prompt}
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
